import { useNavigate } from 'react-router-dom';
import { RingStat } from '@/versions/v1/shared/viz/RingStat';
import { SourceLink } from '@/versions/v1/shared/oem-tiles';
import type { JobSeverity } from '@/versions/v1/shared/oem-engine';
import type { JobCard } from './JobBoardScene.transform';
import { jobRunHref } from './job-nav';

// =========================================================================
// One job card. Fork-owned (zone C), P5b lane.
//
// Port of the demo's `jobCardHTML` (source line 3074): a progress ring, the
// machine and kind, the id and site, the title, the next action, and a link
// into the case-to-closure trace.
//
// The demo made the whole card an `onclick` div with a nested button inside
// it and stopped propagation. That is two controls in one hit target and it
// cannot be operated from a keyboard, so the card here is a `<button>` that
// opens the job (the caller resolves `OemJob.action`) with the run link as a
// sibling underneath rather than a child — same two destinations, two tab
// stops, no propagation games.
//
// UI layer.
// =========================================================================

/** Job severity → the tone token the ring and the top band are drawn in. */
export const SEVERITY_TONE: Readonly<Record<JobSeverity, string>> = {
  crit: 'bad',
  warn: 'warn',
  info: 'flat',
  done: 'good',
};

/** Severity → the word beside the ring, for anyone not reading colour. */
const SEVERITY_LABEL: Readonly<Record<JobSeverity, string>> = {
  crit: 'Critical',
  warn: 'Watch',
  info: 'In train',
  done: 'Closed',
};

export function JobCardTile({
  card,
  onOpen,
}: {
  card: JobCard;
  onOpen: (card: JobCard) => void;
}) {
  const navigate = useNavigate();
  const { job } = card;
  const tone = SEVERITY_TONE[job.sev];

  return (
    <div
      className="relative flex min-w-0 flex-col overflow-hidden rounded-xl border"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[3px]"
        style={{ background: `var(--${job.sev === 'crit' ? 'crit' : job.sev === 'warn' ? 'warn' : job.sev === 'done' ? 'good' : 'hairline-strong'})` }}
      />
      <button
        type="button"
        onClick={() => onOpen(card)}
        className="flex min-w-0 flex-1 items-start gap-3 px-3.5 pb-2 pt-3.5 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
      >
        <RingStat
          pct={job.pct}
          tone={tone}
          size={46}
          label={String(job.pct)}
          title={`${job.id} progress through the operating model`}
        />
        <span className="min-w-0 flex-1">
          <span
            className="block text-[12.5px] font-semibold leading-snug"
            style={{ color: 'var(--ink-primary)' }}
          >
            {card.heading}
          </span>
          <span
            className="mono mt-0.5 block text-[10.5px]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            {`${card.meta} · ${SEVERITY_LABEL[job.sev]}`}
          </span>
          <span
            className="mt-1.5 block text-[11.5px] leading-[1.5]"
            style={{ color: 'var(--ink-secondary)' }}
          >
            {card.title}
          </span>
          <span
            className="mt-1 block text-[11.5px] leading-[1.5]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            {`→ ${card.next}`}
          </span>
          <span
            className="mt-1.5 block text-[10.5px]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            {job.owner}
          </span>
        </span>
      </button>
      <div className="flex flex-wrap items-center gap-3 px-3.5 pb-3">
        <SourceLink onClick={() => navigate(jobRunHref(job))}>
          {card.worked ? 'worked run ›' : 'run ›'}
        </SourceLink>
        {job.machine ? (
          <SourceLink
            onClick={() => navigate(`/machines/${encodeURIComponent(job.machine as string)}`)}
            className="mono"
          >
            {job.machine}
          </SourceLink>
        ) : null}
      </div>
    </div>
  );
}
